import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline'

import { updateConfig, moveBookmark, updateBookmarkGroupName, getConfigByType } from '../api/config'
import LoadingSpinner from '../components/LoadingSpinner'
import BookmarkEditModal from '../components/BookmarkEditModal'
import BookmarkGroupEditModalEnhanced from '../components/BookmarkGroupEditModalEnhanced'

const BookmarksConfig = ({ config }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set())
  const [editingBookmark, setEditingBookmark] = useState(null)
  const [showBookmarkModal, setShowBookmarkModal] = useState(false)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [editingGroupLayout, setEditingGroupLayout] = useState(null)
  // 添加选中状态管理
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedBookmark, setSelectedBookmark] = useState(null)

  const queryClient = useQueryClient()

  // 键盘快捷键支持
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClearSelection()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  // 处理分组选中
  const handleSelectGroup = (groupName, event) => {
    event.stopPropagation()
    if (selectedGroup === groupName) {
      setSelectedGroup(null)
    } else {
      setSelectedGroup(groupName)
      setSelectedBookmark(null) // 清除书签选中状态
    }
  }

  // 处理书签选中
  const handleSelectBookmark = (groupName, bookmarkIndex, event) => {
    event.stopPropagation()
    const bookmarkKey = `${groupName}-${bookmarkIndex}`
    if (selectedBookmark === bookmarkKey) {
      setSelectedBookmark(null)
    } else {
      setSelectedBookmark(bookmarkKey)
      setSelectedGroup(null) // 清除分组选中状态
    }
  }

  // 清除选中状态
  const handleClearSelection = () => {
    setSelectedGroup(null)
    setSelectedBookmark(null)
  }

  // 获取布局配置
  const { data: layoutConfig } = useQuery({
    queryKey: ['settings'],
    queryFn: () => getConfigByType('settings').then(res => res.data)
  })

  const updateConfigMutation = useMutation({
    mutationFn: (data) => updateConfig('bookmarks', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('书签配置已更新')
    },
    onError: (error) => {
      toast.error(`更新失败: ${error.message}`)
    },
  })

  // 移动书签的 mutation
  const moveBookmarkMutation = useMutation({
    mutationFn: ({ groupName, bookmarkIndex, direction }) => moveBookmark(groupName, bookmarkIndex, direction),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('书签顺序已更新')
    },
    onError: (error) => {
      toast.error(`移动失败: ${error.message}`)
    },
  })

  // 更新分组名称的 mutation
  const updateGroupMutation = useMutation({
    mutationFn: ({ oldGroupName, newGroupName }) => updateBookmarkGroupName(oldGroupName, newGroupName),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('分组已更新')
    },
    onError: (error) => {
      toast.error(`更新失败: ${error.message}`)
    },
  })

  // 保存布局配置的 mutation
  const saveLayoutMutation = useMutation({
    mutationFn: (layoutData) => updateConfig('settings', layoutData),
    onSuccess: () => {
      queryClient.invalidateQueries(['settings'])
      toast.success('布局配置已保存')
    },
    onError: (error) => {
      toast.error(`保存布局配置失败: ${error.message}`)
    },
  })

  const toggleGroup = (groupName) => {
    const newExpanded = new Set(expandedGroups)
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName)
    } else {
      newExpanded.add(groupName)
    }
    setExpandedGroups(newExpanded)
  }

  const deleteBookmark = (groupName, bookmarkIndex) => {
    if (!config) return

    if (confirm('确定要删除这个书签吗？')) {
      const newConfig = [...config]
      const group = newConfig.find(g => Object.keys(g)[0] === groupName)

      if (group && group[groupName][bookmarkIndex]) {
        group[groupName].splice(bookmarkIndex, 1)

        updateConfigMutation.mutate(newConfig)
      }
    }
  }

  const deleteGroup = (groupName) => {
    if (!config) return

    if (confirm('确定要删除整个书签分组吗？这将删除组内所有书签。')) {
      const newConfig = config.filter(group => Object.keys(group)[0] !== groupName)
      updateConfigMutation.mutate(newConfig)
    }
  }

  // 获取图标预览URL的函数
  const getIconPreviewUrl = (iconValue) => {
    if (!iconValue || typeof iconValue !== 'string') return null
    try {
      if (iconValue.startsWith('http')) return iconValue
      if (iconValue.endsWith('.png') || iconValue.endsWith('.svg') || iconValue.endsWith('.webp')) {
        return `https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/png/${iconValue}`
      }
      if (iconValue.startsWith('si-')) {
        const iconName = iconValue.replace('si-', '')
        return `https://cdn.jsdelivr.net/npm/simple-icons@v9/${iconName}.svg`
      }
      if (iconValue.startsWith('mdi-')) {
        const iconName = iconValue.replace('mdi-', '')
        return `https://cdn.jsdelivr.net/npm/@mdi/svg@7.2.96/svg/${iconName}.svg`
      }
      return null
    } catch (error) {
      console.error('Error in getIconPreviewUrl:', error)
      return null
    }
  }

  const handleEditBookmark = (groupName, bookmarkIndex, bookmark) => {
    // 创建书签数据的深拷贝，防止引用污染
    const deepCopyBookmark = {}
    Object.keys(bookmark).forEach(key => {
      const bookmarkData = bookmark[key]
      if (Array.isArray(bookmarkData)) {
        // 深拷贝链接数组
        deepCopyBookmark[key] = bookmarkData.map(link => ({ ...link }))
      } else {
        deepCopyBookmark[key] = bookmarkData
      }
    })

    setEditingBookmark({
      groupName,
      bookmarkIndex,
      bookmark: deepCopyBookmark,
    })
    setShowBookmarkModal(true)
  }

  const handleAddGroup = () => {
    setEditingGroup(null)
    setShowGroupModal(true)
  }

  const handleEditGroup = (groupName) => {
    setEditingGroup(groupName)
    // 获取当前分组的布局配置
    const currentLayout = layoutConfig?.layout?.[groupName] || null
    setEditingGroupLayout(currentLayout)
    setShowGroupModal(true)
  }

  const handleMoveBookmark = (groupName, bookmarkIndex, direction) => {
    // 设置当前书签为选中状态
    const currentBookmarkKey = `${groupName}-${bookmarkIndex}`
    setSelectedBookmark(currentBookmarkKey)

    moveBookmarkMutation.mutate({ groupName, bookmarkIndex, direction }, {
      onSuccess: () => {
        // 移动后，选中状态跟随到新位置
        const newIndex = direction === 'up' ? bookmarkIndex - 1 : bookmarkIndex + 1
        const newBookmarkKey = `${groupName}-${newIndex}`
        setSelectedBookmark(newBookmarkKey)
      }
    })
  }

  const handleAddBookmark = (groupName = null) => {
    setEditingBookmark({
      groupName: groupName || (config.length > 0 ? Object.keys(config[0])[0] : ''),
      bookmarkIndex: -1,
      bookmark: null
    })
    setShowBookmarkModal(true)
  }

  const handleSaveBookmark = (groupName, bookmarkData) => {
    if (!config) return

    const newConfig = [...config]
    const originalGroupName = editingBookmark?.groupName
    const originalBookmarkIndex = editingBookmark?.bookmarkIndex

    // 如果是编辑现有书签且切换了分组，需要从原分组删除
    if (originalGroupName && originalBookmarkIndex !== -1 && originalGroupName !== groupName) {
      const originalGroupIndex = newConfig.findIndex(group => Object.keys(group)[0] === originalGroupName)
      if (originalGroupIndex !== -1) {
        const originalGroup = newConfig[originalGroupIndex]
        const originalGroupKey = Object.keys(originalGroup)[0]

        // 从原分组中删除书签
        originalGroup[originalGroupKey].splice(originalBookmarkIndex, 1)
      }
    }

    // 添加到新分组
    const groupIndex = newConfig.findIndex(group => Object.keys(group)[0] === groupName)

    if (groupIndex === -1) {
      // 创建新分组
      newConfig.push({ [groupName]: [bookmarkData] })
    } else {
      const group = newConfig[groupIndex]
      const groupKey = Object.keys(group)[0]

      if (editingBookmark.bookmarkIndex === -1) {
        // 添加新书签
        group[groupKey].push(bookmarkData)
      } else if (originalGroupName === groupName) {
        // 更新现有书签（在同一分组内）
        group[groupKey][editingBookmark.bookmarkIndex] = bookmarkData
      } else {
        // 移动到不同分组，添加到目标分组的最后位置
        group[groupKey].push(bookmarkData)
      }
    }

    updateConfigMutation.mutate(newConfig)
  }

  const handleSaveGroup = (groupName, groupLayoutConfig) => {
    if (editingGroup) {
      // 编辑现有分组
      updateGroupMutation.mutate({ oldGroupName: editingGroup, newGroupName: groupName })

      // 同时保存布局配置
      if (groupLayoutConfig) {
        const newLayoutConfig = { ...layoutConfig }
        if (!newLayoutConfig.layout) {
          newLayoutConfig.layout = {}
        }

        // 如果分组名称改变了，需要删除旧的布局配置
        if (editingGroup !== groupName && newLayoutConfig.layout[editingGroup]) {
          delete newLayoutConfig.layout[editingGroup]
        }

        newLayoutConfig.layout[groupName] = groupLayoutConfig
        saveLayoutMutation.mutate(newLayoutConfig)
      }
    } else {
      // 添加新分组
      if (!config) return

      const newConfig = [...config]
      const newGroup = { [groupName]: [] }
      newConfig.push(newGroup)
      updateConfigMutation.mutate(newConfig)

      // 同时保存布局配置
      if (groupLayoutConfig) {
        const newLayoutConfig = { ...layoutConfig }
        if (!newLayoutConfig.layout) {
          newLayoutConfig.layout = {}
        }
        newLayoutConfig.layout[groupName] = groupLayoutConfig
        saveLayoutMutation.mutate(newLayoutConfig)
      }
    }
  }

  // 处理分组上下移动
  const moveGroup = (index, direction) => {
    // 安全检查
    if (!config || !Array.isArray(config)) {
      toast.error('配置数据无效')
      return
    }

    if (index < 0 || index >= config.length) {
      toast.error('无效的分组索引')
      return
    }

    const newConfig = [...config]
    const targetIndex = direction === 'up' ? index - 1 : index + 1

    if (targetIndex >= 0 && targetIndex < newConfig.length) {
      // 确保两个索引位置的数据都存在
      if (!newConfig[index] || !newConfig[targetIndex]) {
        toast.error('分组数据缺失')
        return
      }

      // 获取当前分组名称并设置为选中状态
      const currentGroupName = Object.keys(newConfig[index])[0]
      setSelectedGroup(currentGroupName)

      // 安全的交换操作
      const temp = newConfig[index]
      newConfig[index] = newConfig[targetIndex]
      newConfig[targetIndex] = temp

      // 直接调用 updateConfig API
      updateConfig('bookmarks', newConfig).then(() => {
        queryClient.invalidateQueries(['config'])
        toast.success('分组顺序已更新')

        // 移动后保持选中状态
        setSelectedGroup(currentGroupName)
      }).catch((error) => {
        toast.error(`更新顺序失败: ${error.message}`)
      })
    } else {
      toast.error('无法移动到指定位置')
    }
  }

  // 获取所有分组名称
  const availableGroups = config ? config.map(group => Object.keys(group)[0]) : []

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6" onClick={handleClearSelection}>
      {/* 页面标题和操作 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">书签配置</h1>
          <p className="mt-2 text-gray-400">
            管理导航页上的书签链接和分组
          </p>
          <p className="mt-1 text-xs text-gray-500">
            💡 点击分组或书签项可选中并高亮显示，按 ESC 键或点击空白区域取消选中
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleAddGroup}
            className="btn-secondary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            添加分组
          </button>
          <button
            onClick={() => handleAddBookmark()}
            className="btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            添加书签
          </button>
        </div>
      </div>

      {/* 书签列表 */}
      <div className="space-y-4">
        {config.map((group, groupIndex) => {
          const groupName = Object.keys(group)[0]
          const bookmarks = group[groupName] || []
          const isExpanded = expandedGroups.has(groupName)
          const isGroupSelected = selectedGroup === groupName

          return (
            <div
              key={groupIndex}
              className={`card transition-all duration-300 ${isGroupSelected
                ? 'ring-2 ring-blue-400 bg-gray-800/80 enhanced-glow-blue glow-selected-group'
                : 'hover:bg-gray-800/50'
                }`}
              onClick={(e) => handleSelectGroup(groupName, e)}
            >
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  toggleGroup(groupName)
                }}
              >
                <div className="flex items-center">
                  {isExpanded ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-400 mr-2" />
                  )}
                  <h3 className="text-lg font-semibold text-white">
                    {groupName}
                  </h3>
                  <span className="ml-3 badge-info">
                    {bookmarks.length} 个书签
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {/* 分组移动按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      moveGroup(groupIndex, 'up')
                    }}
                    disabled={groupIndex === 0}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="上移分组"
                  >
                    <ArrowUpIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      moveGroup(groupIndex, 'down')
                    }}
                    disabled={groupIndex === config.length - 1}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="下移分组"
                  >
                    <ArrowDownIcon className="h-4 w-4" />
                  </button>

                  {/* 分组操作按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAddBookmark(groupName)
                    }}
                    className="btn-secondary btn-sm"
                    title="添加书签"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditGroup(groupName)
                    }}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg transition-colors"
                    title="编辑分组"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteGroup(groupName)
                    }}
                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded-lg transition-colors"
                    title="删除分组"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {bookmarks.map((bookmark, bookmarkIndex) => {
                    const bookmarkName = Object.keys(bookmark)[0]
                    const bookmarkLinks = bookmark[bookmarkName] // 书签链接数组
                    const bookmarkData = bookmarkLinks[0] // 显示第一个链接的信息
                    const bookmarkKey = `${groupName}-${bookmarkIndex}`
                    const isBookmarkSelected = selectedBookmark === bookmarkKey

                    return (
                      <div
                        key={bookmarkIndex}
                        className={`rounded-lg p-4 border transition-all duration-300 cursor-pointer ${isBookmarkSelected
                          ? 'bg-gray-600 border-green-400 ring-2 ring-green-400 enhanced-glow-green glow-selected-service'
                          : 'bg-gray-700 border-gray-600 hover:bg-gray-600 hover:border-gray-500'
                          }`}
                        onClick={(e) => handleSelectBookmark(groupName, bookmarkIndex, e)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-medium">
                            {bookmarkName}
                          </h4>
                          <div className="flex items-center space-x-1">
                            {/* 书签移动按钮 */}
                            <button
                              onClick={() => handleMoveBookmark(groupName, bookmarkIndex, 'up')}
                              disabled={bookmarkIndex === 0}
                              className="p-1 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="上移"
                            >
                              <ArrowUpIcon className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleMoveBookmark(groupName, bookmarkIndex, 'down')}
                              disabled={bookmarkIndex === bookmarks.length - 1}
                              className="p-1 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="下移"
                            >
                              <ArrowDownIcon className="h-3 w-3" />
                            </button>

                            {/* 书签操作按钮 */}
                            {bookmarkData.href && (
                              <a
                                href={bookmarkData.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-gray-400 hover:text-primary-400 transition-colors"
                                title="访问链接"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </a>
                            )}
                            <button
                              onClick={() => handleEditBookmark(groupName, bookmarkIndex, bookmark)}
                              className="p-1 text-gray-400 hover:text-white transition-colors"
                              title="编辑书签"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteBookmark(groupName, bookmarkIndex)}
                              className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                              title="删除书签"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="space-y-1">
                          {bookmarkData.abbr && (
                            <div className="flex items-center">
                              <span className="text-xs text-gray-400 w-12">缩写:</span>
                              <span className="text-sm text-gray-300 font-mono bg-gray-600 px-2 py-1 rounded">
                                {bookmarkData.abbr}
                              </span>
                            </div>
                          )}
                          {bookmarkData.icon && (
                            <div className="flex items-center">
                              <span className="text-xs text-gray-400 w-12">图标:</span>
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center border border-gray-600">
                                  {getIconPreviewUrl(bookmarkData.icon) ? (
                                    <img
                                      src={getIconPreviewUrl(bookmarkData.icon)}
                                      alt="图标预览"
                                      className="w-4 h-4 object-contain"
                                      onError={(e) => {
                                        e.target.style.display = 'none'
                                        e.target.nextSibling.style.display = 'block'
                                      }}
                                    />
                                  ) : null}
                                  <span
                                    className="text-xs text-gray-500 hidden"
                                    style={{ display: getIconPreviewUrl(bookmarkData.icon) ? 'none' : 'block' }}
                                  >
                                    ?
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 font-mono">
                                  {bookmarkData.icon}
                                </span>
                              </div>
                            </div>
                          )}
                          {bookmarkData.href && (
                            <div className="flex items-center">
                              <span className="text-xs text-gray-400 w-12">链接:</span>
                              <span className="text-xs text-gray-500 truncate">
                                {bookmarkData.href}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}

                  {bookmarks.length === 0 && (
                    <div className="col-span-full text-center py-8 text-gray-400">
                      <p>此分组暂无书签</p>
                      <button
                        onClick={() => setShowAddModal(true)}
                        className="mt-2 btn-primary btn-sm"
                      >
                        添加第一个书签
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {config.length === 0 && (
          <div className="card text-center py-12">
            <h3 className="text-lg font-medium text-gray-300 mb-2">
              暂无书签配置
            </h3>
            <p className="text-gray-400 mb-4">
              开始添加你的第一个书签分组
            </p>
            <button
              onClick={() => setShowAddGroupModal(true)}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              创建分组
            </button>
          </div>
        )}
      </div>

      {/* 书签格式说明 */}
      <div className="card">
        <h3 className="card-title">书签格式说明</h3>
        <div className="mt-4 space-y-2 text-sm text-gray-400">
          <p>• <strong>缩写 (abbr)</strong>: 显示在书签按钮上的简短文本</p>
          <p>• <strong>图标 (icon)</strong>: 书签的图标，支持多种格式：</p>
          <div className="ml-4 space-y-1 text-xs">
            <p>- 本地图标: <code className="bg-gray-700 px-1 rounded">plex.png</code></p>
            <p>- Simple Icons: <code className="bg-gray-700 px-1 rounded">si-github</code></p>
            <p>- Material Design Icons: <code className="bg-gray-700 px-1 rounded">mdi-github</code></p>
            <p>- 自定义URL: <code className="bg-gray-700 px-1 rounded">https://example.com/icon.png</code></p>
          </div>
          <p>• <strong>链接 (href)</strong>: 书签指向的 URL 地址</p>
          <p>• 书签会按照分组显示，每个分组可以包含多个书签</p>
          <p>• 建议使用简洁的缩写，如 "GH" 代表 GitHub</p>
        </div>
      </div>

      {/* 书签编辑模态框 */}
      <BookmarkEditModal
        isOpen={showBookmarkModal}
        onClose={() => {
          setShowBookmarkModal(false)
          setEditingBookmark(null)
        }}
        onSave={handleSaveBookmark}
        bookmark={editingBookmark?.bookmark}
        groupName={editingBookmark?.groupName || ''}
        availableGroups={availableGroups}
      />

      {/* 分组编辑模态框 */}
      <BookmarkGroupEditModalEnhanced
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false)
          setEditingGroup(null)
          setEditingGroupLayout(null)
        }}
        onSave={handleSaveGroup}
        group={editingGroup}
        groupLayout={editingGroupLayout}
        existingGroups={availableGroups}
      />
    </div>
  )
}

export default BookmarksConfig
